import { Options, Vue } from 'vue-class-component';
import store from '@/store/store';
import { nextTick, PropType } from 'vue';
import VueScrollTo from 'vue-scrollto';
import { mapState } from 'vuex';
import VerticalStepComponent from './VerticalStepComponent.vue';
import { PROPOSAL_INFO_ERROR, PROPOSAL_LOADED } from '@/store/modules/ProposalContext';
import { isInView } from '@/services/uiService';
import { getInsuranceCompanies, submitConsent, submitProposal } from '@/services/apiService';
import ModelMapper from './ModelMapper';
import { formatterDigit } from '@/services/FormatService';
import { updateConsentFromApi } from '@/services/consentService';
import { STEP_TYPE, trackError, trackSignicat, trackStep } from '@/services/TrackService';
import { Log } from '@/appinsights/logging/log';
import { mapProductHighlightField } from '@/services/FieldService';
import { CmsPolicy } from '@/types/types';
import { AcceptPage } from '@/cms/definitions/content-types';
import { loadProposal, resolvePageLoadingError, resolveModalError } from '@/services/PageService';
import { PROPOSAL_STEPS } from '@/types/enums';

@Options({
	name: 'VerticalContainerComponent',
	components: {
		VerticalStepComponent,
	},
	computed: mapState<any>({
		party: (state) => state.ProposalContext.party,
		quote_id: (state) => state.ProposalContext.quote_id,
		price: (state) => state.ProposalContext.price,
		proposalSteps: (state) => store.state.ProposalContext.proposalSteps,
	}),
	props: {
		model: Object as PropType<AcceptPage>,
	},
})
export default class VerticalContainerComponent extends Vue {
	private submitTime = 'NA';
	public model!: AcceptPage;

	public party!: any; // KLK types
	public quote_id!: string;
	public price!: number;
	public proposalSteps!: Map<string, CmsPolicy>;

	public renderPage = false;
	public showPage = false;
	private waitActive = true;
	private activeStep = undefined;

	public visibleError = false;
	public loadError = null;

	public created() {
		addEventListener('keyup', this.moveOn);
	}
	public beforeUnmount() {
		removeEventListener('keyup', this.moveOn);
	}

	public moveOn(evt) {
		if (evt.keyCode === 13 && this.activeStep.isValid) {
			this.nextStep(this.activeStep);
		}
	}

	public mounted() {
		this.init();
	}

	private async init() {
		store.state.hasError = false;
		store.state.mockedDataLoaded = false;
		let proposal;
		try {
			proposal = await loadProposal();

			const insuranceCompanies = await getInsuranceCompanies();
			store.state.ProposalContext.insuranceCompanies = insuranceCompanies?.data?.insurance_companies;

			store.state.ProposalContext.originalRequest = proposal.data;

			const modelMapper: ModelMapper = await ModelMapper.factory(proposal, this.model);
			await modelMapper.mapProposalSteps();

			// get user consent choses from API - no need to wait
			updateConsentFromApi();

			this.activeStep = store.getters.getActiveProposalStep(undefined);
			this.renderPage = true;
		} catch (error) {
			Log.exception('Error mounting proposal', error, { quoteId: store.state.ProposalContext.quote_id });

			store.state.showSpinner = false;
			nextTick(() => {
				// KLK try get error code
				this.loadError = resolvePageLoadingError(proposal?.data?.type);
			});
			trackError('ProposalLoadError', error.message);
		} finally {
			setTimeout(() => {
				store.state.showSpinner = false;
				// make sure activeStep is not loaded immediately - enables transition
				this.waitActive = false;
				this.showPage = true;
				trackStep(this.activeStep, STEP_TYPE.START);
			}, 100);

			await store.dispatch(PROPOSAL_LOADED);
		}
	}

	public isActiveStep(step: any): boolean {
		return step.id === this.activeStep?.id && !this.waitActive;
	}

	public isValid(step: any): boolean {
		return step.isValid;
	}

	public async gotoStep(step: any): Promise<void> {
		// KLK await updateConsentFromApi(); when consentBlock is pressed on
		// KLK await submitConsentToApi(); when consentBlock is currentStep and customer is leaving

		const previousStepsValid: boolean = store.getters.getIsPreviousProposalStepsValid(step.id);
		if (previousStepsValid) {
			// track current step as selected
			await trackStep(this.activeStep, STEP_TYPE.SELECT);

			this.activeStep = step;

			// track new step
			await trackStep(this.activeStep, STEP_TYPE.PROGRESS);

			if (this.activeStep.id === PROPOSAL_STEPS.summaryBlock) {
				mapProductHighlightField(store.state.ProposalContext.proposalSteps.get(PROPOSAL_STEPS.summaryBlock));
			}

			this.scrollMeTo(this.activeStep);
			this.visibleError = false;
			this.reloadLoop();
		} else {
			store.dispatch(PROPOSAL_INFO_ERROR, true);
			this.visibleError = true;
		}
	}

	public renderBackButton(): boolean {
		return store.getters.getPreviousProposalStep(this.activeStep.id);
	}

	public async prevStep(step: any) {
		const previousStep = store.getters.getPreviousProposalStep(step.id);
		this.activeStep = previousStep;

		if (this.activeStep.id === PROPOSAL_STEPS.summaryBlock) {
			mapProductHighlightField(store.state.ProposalContext.proposalSteps.get(PROPOSAL_STEPS.summaryBlock));
		}
		this.visibleError = false;
		this.scrollMeTo(this.activeStep);
		this.reloadLoop();
	}

	public async nextStep(step: any) {
		if (!step.isValid) {
			store.dispatch(PROPOSAL_INFO_ERROR, true);
			this.visibleError = true;
			return;
		}

		const active = store.getters.getActiveProposalStep(step.id);

		if (!active) {
			// go to Signicat for MitId signature
			this.visibleError = false;
			// all steps are valid
			store.state.showSpinner = true;
			let time = Date.now();
			await submitConsent();

			// track finished digital accept
			await trackStep({ title: 'End flow' }, STEP_TYPE.COMPLETE);

			const patchRes = await submitProposal();

			time = (Date.now() - time) / 1000;
			this.submitTime = formatterDigit.format(time) + ' sek.';
			if (patchRes?.status === 200 && patchRes.data?.signicat_signing_url) {
				await trackSignicat(true);
				location.href = patchRes.data.signicat_signing_url;
			} else {
				// KLK track error(?)
				setTimeout(() => {
					resolveModalError(patchRes?.data?.type);
					store.state.showSpinner = false;
				}, 100);
			}
			return;
		}

		// track current step as selected
		await trackStep(this.activeStep, STEP_TYPE.SELECT);

		this.activeStep = active;

		// track new step
		await trackStep(this.activeStep, STEP_TYPE.PROGRESS);

		if (this.activeStep.id === PROPOSAL_STEPS.summaryBlock) {
			mapProductHighlightField(store.state.ProposalContext.proposalSteps.get(PROPOSAL_STEPS.summaryBlock));
		}
		this.visibleError = false;
		this.scrollMeTo(this.activeStep);
		this.reloadLoop();
	}

	public scrollMeTo(step: any) {
		if (!step?.id) {
			return;
		}
		setTimeout(() => {
			if (!isInView(`_${step.id}`)) {
				VueScrollTo.scrollTo(`#_${step.id}`, 500, { easing: 'ease-out', offset: -15 });
			}
		}, 750);
	}
	private reloadLoop() {
		this.renderPage = !this.renderPage;
		this.renderPage = !this.renderPage;
	}
}
